import { useMemo } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useCreateGcrs, useGcrFormOptions } from '../api';
import { defaultFormValues, GcrForm } from './gcr-form';

interface CreateGcrDialogProps {
  isOpen: boolean;
  typeId?: string;
  sizeId?: string;
  printerId?: string;
  onSuccess: () => Promise<any>;
  onOpenChange: (open: boolean) => void;
}

export function CreateGcrDialog({
  isOpen,
  onOpenChange,
  typeId,
  sizeId,
  printerId,
  onSuccess,
}: CreateGcrDialogProps) {
  const { optionsData } = useGcrFormOptions();
  const { createGcrMutation, uploadUrlData, refetchUploadUrl } =
    useCreateGcrs();

  const refetchUploadCredentials = async () => {
    try {
      await refetchUploadUrl();
    } catch (error) {
      toast.error('Uh oh! Something went wrong.');
    }
  };

  const saveGcr = async (data) => {
    if (uploadUrlData?.gcrId) {
      try {
        await createGcrMutation.mutateAsync({
          ...data,
          id: uploadUrlData.gcrId,
        });
        await onSuccess();
        toast.success(`GCR file “${data.name}” added successfully!`);
        onOpenChange(false);
      } catch (error) {
        console.error(error.message);
      }
    }
  };

  const defaultValues = useMemo(
    () => ({
      ...defaultFormValues,
      typeId,
      sizeId,
      printerId,
    }),
    [typeId, sizeId, printerId]
  );

  return (
    <Dialog onOpenChange={onOpenChange} open={isOpen}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add New GCR File</DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <GcrForm
          denseMode
          onSubmit={saveGcr}
          options={optionsData}
          defaultValues={defaultValues}
          uploadUrlData={uploadUrlData}
          formClassName="flex flex-col-reverse"
          refetchUploadCredentials={refetchUploadCredentials}
        >
          {({ isLoading }) => (
            <DialogFooter className="mt-6">
              <DialogClose asChild>
                <Button
                  variant="outline"
                  className="w-[130px]"
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="submit"
                isLoading={isLoading}
                className="w-[130px]"
                data-testid="save-button"
              >
                Save changes
              </Button>
            </DialogFooter>
          )}
        </GcrForm>
      </DialogContent>
    </Dialog>
  );
}
